import { createFileRoute } from '@tanstack/react-router';
import { Ban, Plus, X } from 'lucide-react';
import { type FormEvent, useState } from 'react';

import { PageShell } from '@/components/app/page-shell';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useGetClaireBlockedCampaigns,
  useUpdateClaireBlockedCampaigns,
} from '@/features/chatbots/api';
import { useActiveOrganization } from '@/features/organization';

/**
 * `/dashboard/settings/claire-campaigns` — Meta ad campaigns Claire stays out
 * of. A lead form or click-to-message conversation from one of these campaigns
 * is handed straight to the team instead of getting a reply from Claire.
 */
export const Route = createFileRoute(
  '/_authed/dashboard/settings/claire-campaigns'
)({
  component: ClaireCampaignsSettingsPage,
});

const CAMPAIGN_ID_PATTERN = /^\d{5,30}$/;

/** Accepts one id or a pasted list split by commas, spaces or new lines. */
function parseCampaignIds(raw: string): { ids: string[]; invalid: string[] } {
  const tokens = raw
    .split(/[\s,;]+/)
    .map((t) => t.trim())
    .filter(Boolean);
  return {
    ids: tokens.filter((t) => CAMPAIGN_ID_PATTERN.test(t)),
    invalid: tokens.filter((t) => !CAMPAIGN_ID_PATTERN.test(t)),
  };
}

function ClaireCampaignsSettingsPage() {
  return (
    <PageShell className="pt-10 pb-8" maxWidth="max-w-[960px]">
      <title>Claire ad campaigns | Borrah</title>

      <header className="space-y-1">
        <h2 className="font-semibold text-xl tracking-tight">
          Claire ad campaigns
        </h2>
        <p className="text-muted-foreground text-sm">
          Choose which Meta ad campaigns Claire should leave to your team.
        </p>
      </header>

      <BlockedCampaignsCard />
    </PageShell>
  );
}

function BlockedCampaignsCard() {
  const { data: activeOrg } = useActiveOrganization();
  const organizationId = activeOrg?.id ?? '';
  const { campaignIds, isLoading, isError, error } =
    useGetClaireBlockedCampaigns(organizationId);
  const { updateBlockedCampaigns, isUpdating } =
    useUpdateClaireBlockedCampaigns();

  const [draft, setDraft] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);

  const save = (next: string[], onSuccess?: () => void) =>
    updateBlockedCampaigns(
      { organizationId, campaignIds: next },
      { onSuccess }
    );

  const handleAdd = (event: FormEvent) => {
    event.preventDefault();
    const { ids, invalid } = parseCampaignIds(draft);
    if (invalid.length > 0) {
      setInputError(
        `${invalid.join(', ')} ${invalid.length === 1 ? "isn't" : "aren't"} a campaign ID. Campaign IDs are numbers only, e.g. 120210123456789012.`
      );
      return;
    }
    const fresh = ids.filter((id) => !campaignIds.includes(id));
    if (fresh.length === 0) {
      setInputError(
        ids.length > 0
          ? 'That campaign is already blocked.'
          : 'Enter a campaign ID.'
      );
      return;
    }
    setInputError(null);
    save([...campaignIds, ...fresh], () => setDraft(''));
  };

  const handleRemove = (id: string) =>
    save(campaignIds.filter((existing) => existing !== id));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Campaigns Claire won&apos;t reply to</CardTitle>
        <CardDescription>
          When someone fills in a lead form or messages you from an ad in one of
          these campaigns, Claire won&apos;t reply. The conversation goes
          straight to your inbox for your team. You can find a campaign&apos;s
          ID in Meta Ads Manager, in the campaign&apos;s settings or the
          &ldquo;Campaign ID&rdquo; column.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <form onSubmit={handleAdd}>
          <Field data-invalid={!!inputError}>
            <FieldLabel htmlFor="campaign-id">Campaign ID</FieldLabel>
            <div className="flex gap-2">
              <Input
                id="campaign-id"
                inputMode="numeric"
                autoComplete="off"
                placeholder="e.g. 120210123456789012"
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  if (inputError) setInputError(null);
                }}
                aria-invalid={!!inputError}
                disabled={!organizationId || isUpdating}
              />
              <Button
                type="submit"
                disabled={!organizationId || isUpdating || !draft.trim()}
              >
                <Plus />
                Add
              </Button>
            </div>
            <FieldDescription>
              Paste several at once, separated by commas or new lines.
            </FieldDescription>
            {inputError && <FieldError>{inputError}</FieldError>}
          </Field>
        </form>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : isError ? (
          <Alert variant="destructive">
            <AlertDescription>
              {error?.message || "Couldn't load blocked campaigns."}
            </AlertDescription>
          </Alert>
        ) : campaignIds.length === 0 ? (
          <Empty className="border border-dashed">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Ban />
              </EmptyMedia>
              <EmptyTitle>No blocked campaigns</EmptyTitle>
              <EmptyDescription>
                Claire replies to leads from all of your ad campaigns.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <ul className="divide-y rounded-md border">
            {campaignIds.map((id) => (
              <li
                key={id}
                className="flex items-center justify-between gap-2 px-3 py-2"
              >
                <span className="font-mono text-sm">{id}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Unblock campaign ${id}`}
                  disabled={isUpdating}
                  onClick={() => handleRemove(id)}
                >
                  <X />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
